import { QuestionnaireCategoryItemsItemOneOf as Question, Questionnaire } from '@/api/generated-catch-backend/model';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { isQuestion } from '../schemas/questionnaire-schema';
import { DeselectDisplay } from './deselect-display/deselect-display';
import { ErrorDisplay } from './error-display/error-display';
import { QuestionnaireCard } from './questionnaire-card/questionnaire-card';
import { Header } from './questionnaire-header/header';
import { QuestionnaireWrapper } from './questionnaire-wrapper';
import { StickyFooterBar } from './sticky-footer-bar/sticky-footer-bar';
import { NextButton } from './survey-item/components/next-button/button';
import { InterstitialSkeleton } from './survey-item/components/interstitial/interstitial-skeleton';
import { SurveyItem } from './survey-item/survey-item';

// Context and hooks
import { QuestionnaireProvider, useQuestionnaireContext } from '../context/QuestionnaireContext';
import { useQuestionnaireForm } from '../hooks/use-questionnaire-form';
import { useQuestionnaireLoading } from '../hooks/use-questionnaire-loading';
import { useQuestionnaireNavigation } from '../hooks/use-questionnaire-navigation';
import { useQuestionnaireSession } from '../hooks/use-questionnaire-session';
import { useQuestionInterceptor } from '../hooks/use-question-interceptor';
import { InterceptorResult } from '../interceptors/types';
import { extractLottieUrls, usePrefetchLotties } from '../utils/lottie-queries';
import { ThreeDotLoader } from './loader/loader';
import { OnNext } from './types';

export interface QuestionnaireAnalyticsHandlers {
  onPageViewed?: (slug: string) => void;
  onAnswerSubmitted?: (slug: string, answerValue: string) => void;
}

interface QuestionnaireContentProps {
  questionnaire?: Questionnaire;
  onComplete?: () => void;
  analyticsHandlers?: QuestionnaireAnalyticsHandlers;
}

function QuestionnaireContent({ questionnaire: providedQuestionnaire, onComplete, analyticsHandlers }: QuestionnaireContentProps) {
  const { state, dispatch } = useQuestionnaireContext();
  const questionTitleRef = useRef<HTMLDivElement>(null);

  // Initialize hooks
  useQuestionnaireLoading(providedQuestionnaire);
  const {
    selectedQuestionnaire,
    questions,
    questionToCategory,
    getOrCreateResponse,
    responseWithAnswers,
    resetSession,
  } = useQuestionnaireSession(providedQuestionnaire);

  const {
    form,
    resetField,
    handleNext: formHandleNext,
    handleFormSubmit,
    getValues,
    FormProvider,
    isQuestionAnswered,
    loadFormDataFromAnswers,
  } = useQuestionnaireForm(
    getOrCreateResponse,
    async () => {
      await resetSession();
    },
    onComplete,
    analyticsHandlers,
  );

  const { currentQuestionIndex, questionnaireWrapperRef, handleBack, isNavigationBusy, canGoPrevious } = useQuestionnaireNavigation(
    resetField,
    isQuestionAnswered,
  );

  // Prefetch Lottie animations
  const prefetchLotties = usePrefetchLotties();

  // Current question and answer
  const currentQuestion = questions[currentQuestionIndex];

  // Check if current question should be intercepted
  const interceptorMatch = useQuestionInterceptor(currentQuestion);

  // Lock body scroll to prevent iOS Safari pull-to-refresh + set accurate viewport height
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.width = '100%';
    body.style.height = '100%';

    const setAppHeight = () => {
      document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
    };
    setAppHeight();
    window.addEventListener('resize', setAppHeight);

    return () => {
      html.style.overflow = '';
      body.style.overflow = '';
      body.style.position = '';
      body.style.width = '';
      body.style.height = '';
      document.documentElement.style.removeProperty('--app-height');
      window.removeEventListener('resize', setAppHeight);
    };
  }, []);

  // Track page viewed when current question changes
  const analyticsHandlersRef = useRef(analyticsHandlers);
  analyticsHandlersRef.current = analyticsHandlers;
  useEffect(() => {
    if (currentQuestion?.slug && analyticsHandlersRef.current?.onPageViewed) {
      analyticsHandlersRef.current.onPageViewed(currentQuestion.slug);
    }
  }, [currentQuestion?.slug]);

  // Load form data from backend when responseWithAnswers first becomes available (session restoration).
  // Only runs once per session — subsequent form updates happen via react-hook-form's own state.
  // Re-running on every `questions` change would reset the form to stale cached answers,
  // overwriting the user's fresh selections (confirmed via debugging).
  const hasRestoredFormData = useRef(false);
  useEffect(() => {
    if (responseWithAnswers?.items?.length && !hasRestoredFormData.current) {
      loadFormDataFromAnswers(responseWithAnswers.items, questions);
      hasRestoredFormData.current = true;
    }
  }, [responseWithAnswers?.items?.length, loadFormDataFromAnswers, questions]);

  useEffect(() => {
    form.clearErrors();
  }, [currentQuestionIndex, form]);

  const memoizedLottieUrls = useMemo(() => extractLottieUrls(state.questions), [state.questions]);
  useEffect(() => {
    if (questions.length > 0) {
      if (memoizedLottieUrls.length > 0) {
        prefetchLotties(memoizedLottieUrls);
      }
    }
  }, [questions, prefetchLotties, memoizedLottieUrls]);

  // Combine navigation and form handling
  const handleNext: OnNext = useCallback(
    (args) => {
      formHandleNext(args);
    },
    [formHandleNext],
  );

  // Listen for email-captured event from email-only questions
  // TODO: Refactor questions to optionally render progress and wrapper so we don't have to do this
  useEffect(() => {
    const handleEmailCaptured = () => {
      handleNext({ skipQuestion: false });
    };

    document.addEventListener('email-captured', handleEmailCaptured);
    return () => {
      document.removeEventListener('email-captured', handleEmailCaptured);
    };
  }, [handleNext]);

  // Category progress calculation
  const currentCategoryIndex = questionToCategory.get(currentQuestionIndex) || 0;
  const questionsOnly = questions.filter(isQuestion);

  // Calculate the index within questions-only array
  const currentQuestionOnlyIndex = questions.slice(0, currentQuestionIndex + 1).filter(isQuestion).length - 1;

  if (!selectedQuestionnaire || questions.length === 0) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-black">
        <ThreeDotLoader />
      </div>
    );
  }

  // Wait for session restore before rendering to prevent flashing the wrong question.
  // Exception: if the current item is a loader interstitial, render it immediately
  // since it serves as a natural loading screen.
  const currentItemIsLoader =
    currentQuestion?.questionnaireItemKind === 'interstitial' &&
    'type' in currentQuestion &&
    currentQuestion.type === 'loader';

  if (!state.hasRestoredSession && !currentItemIsLoader) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-black">
        <ThreeDotLoader />
      </div>
    );
  }

  // Handle intercepted questions - render interceptor component
  if (interceptorMatch) {
    const InterceptorComponent = interceptorMatch.interceptor.component;

    const handleInterceptorComplete = (result: InterceptorResult) => {
      if (result.shouldContinue) {
        // Synthetic answer - submit and move to next question
        handleNext({
          skipQuestion: false,
          syntheticAnswer: result.answerValue,
          metadata: result.metadata,
        });
      }
    };

    return (
      <InterceptorComponent
        context={interceptorMatch.context}
        onComplete={handleInterceptorComplete}
      />
    );
  }

  // Predicted next question for skeleton transitions
  const nextQuestion = questions[currentQuestionIndex + 1];

  // When skeleton is active, choose layout based on the DESTINATION item's type
  // so the skeleton previews what's coming, not what's currently showing.
  const isCurrentlyInterstitial = currentQuestion?.questionnaireItemKind === 'interstitial';
  const useInterstitialLayout = state.skeletonQuestion && nextQuestion
    ? nextQuestion.questionnaireItemKind === 'interstitial'
    : isCurrentlyInterstitial;

  // Handle interstitial layout (current interstitial OR skeleton transitioning TO interstitial)
  if (useInterstitialLayout) {
    const showInterstitialSkeleton = !!state.skeletonQuestion;

    // Use next question's slug as skeleton key to prevent double swipe
    const interstitialSkeletonKey = nextQuestion?.slug ?? 'interstitial-skeleton';

    return (
      <FormProvider {...form}>
        <QuestionnaireWrapper ref={questionnaireWrapperRef}>
          <div className="h-screen w-full">
            <Header
              categoryName={selectedQuestionnaire.categories[currentCategoryIndex].name}
              currentQuestionOnlyIndex={currentQuestionOnlyIndex}
              questions={questionsOnly}
              isInterstitial={true}
              totalCategories={selectedQuestionnaire.categories.length}
              currentCategoryIndex={currentCategoryIndex}
              currentQuestionIndex={currentQuestionIndex}
              questionToCategory={questionToCategory}
            />
            <AnimatePresence mode="wait">
              {showInterstitialSkeleton ? (
                <motion.div
                  key={interstitialSkeletonKey}
                  initial={{ x: state.navigationDirection === 'forward' ? 50 : -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: state.navigationDirection === 'forward' ? -50 : 50, opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  className="h-full w-full"
                >
                  <InterstitialSkeleton />
                </motion.div>
              ) : (
                <motion.div
                  key={currentQuestion.slug}
                  initial={{ x: state.navigationDirection === 'forward' ? 50 : -50, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: state.navigationDirection === 'forward' ? -50 : 50, opacity: 0 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                  className="h-full w-full"
                >
                  <SurveyItem
                    surveyItem={currentQuestion}
                    onWhyWeAskExpand={() => dispatch({ type: 'SET_WHY_WE_ASK_EXPANDED', payload: true })}
                    hideWhyWeAsk={state.isWhyWeAskExpanded}
                    onNext={handleNext}
                    onBack={canGoPrevious ? handleBack : undefined}
                    isBackDisabled={isNavigationBusy}
                  />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </QuestionnaireWrapper>
      </FormProvider>
    );
  }

  // Handle email-only questions - render full screen with form context
  if (
    currentQuestion &&
    currentQuestion.questionnaireItemKind === 'question' &&
    currentQuestion.questionType === 'email-only'
  ) {
    return (
      <FormProvider {...form}>
        <form onSubmit={handleFormSubmit}>
          <QuestionnaireWrapper ref={questionnaireWrapperRef}>
            <div className="h-screen w-full">
              <SurveyItem
                key={currentQuestion.slug}
                surveyItem={currentQuestion}
                onWhyWeAskExpand={() => dispatch({ type: 'SET_WHY_WE_ASK_EXPANDED', payload: true })}
                hideWhyWeAsk={state.isWhyWeAskExpanded}
                onNext={handleNext}
              />
            </div>
          </QuestionnaireWrapper>
        </form>
      </FormProvider>
    );
  }

  const currentAnswer = currentQuestion ? getValues(currentQuestion.slug) : undefined;

  // Show skeleton during transitions — always activate when skeletonQuestion is set.
  // If the next item is a question, swap to show it in skeleton form (smooth question→question).
  // If the next item is an interstitial, show the current question in skeleton form as loading feedback.
  const isNextItemAQuestion = nextQuestion && isQuestion(nextQuestion);
  const isQuestionLoading = !!state.skeletonQuestion;
  const displayQuestion = (isQuestionLoading && isNextItemAQuestion)
    ? (nextQuestion as Question)
    : (currentQuestion as Question);

  return (
    <FormProvider {...form}>
      <form onSubmit={handleFormSubmit} className="fixed top-0 left-0 right-0 overflow-hidden overscroll-none bg-white" style={{ height: 'var(--app-height, 100dvh)' }}>
        <QuestionnaireWrapper
          ref={questionnaireWrapperRef}
          className="relative flex h-dvh w-screen flex-col overflow-hidden bg-white"
        >
          {/* Header - fixed height */}
          <div className="flex-shrink-0">
            <Header
              categoryName={selectedQuestionnaire.categories[currentCategoryIndex].name}
              currentQuestionOnlyIndex={currentQuestionOnlyIndex}
              questions={questionsOnly}
              totalCategories={selectedQuestionnaire.categories.length}
              currentCategoryIndex={currentCategoryIndex}
              currentQuestionIndex={currentQuestionIndex}
              questionToCategory={questionToCategory}
              isLoading={isQuestionLoading}
            />
          </div>

          {/* Content + Footer wrapper */}
          <div className="flex min-h-0 flex-1 w-full flex-col overflow-hidden bg-white">
            <QuestionnaireCard
              isWhyWeAskExpanded={state.isWhyWeAskExpanded}
              setIsWhyWeAskExpanded={(expanded: boolean) =>
                dispatch({ type: 'SET_WHY_WE_ASK_EXPANDED', payload: expanded })
              }
              currentQuestion={displayQuestion}
              handleNext={handleNext}
              questionTitleRef={questionTitleRef}
              isLoading={isQuestionLoading}
              navigationDirection={state.navigationDirection}
              onSkip={() => handleNext({ skipQuestion: true })}
            />

            {/* Footer - normal flow, outside scroll area */}
            {!state.isWhyWeAskExpanded && (
              <StickyFooterBar
                variant="light"
                onBack={canGoPrevious ? handleBack : undefined}
                isBackDisabled={isNavigationBusy}
                topContent={
                  <>
                    <ErrorDisplay />
                    <DeselectDisplay />
                  </>
                }
              >
                <NextButton onNext={() => handleNext({ skipQuestion: false })} />
              </StickyFooterBar>
            )}
          </div>
        </QuestionnaireWrapper>
      </form>
    </FormProvider>
  );
}

interface QuestionnaireV2RefPageProps {
  questionnaire?: Questionnaire;
  onComplete?: () => void;
  analyticsHandlers?: QuestionnaireAnalyticsHandlers;
}

export default function QuestionnaireV2RefPage({ questionnaire, onComplete, analyticsHandlers }: QuestionnaireV2RefPageProps) {
  return (
    <QuestionnaireProvider>
      <QuestionnaireContent questionnaire={questionnaire} onComplete={onComplete} analyticsHandlers={analyticsHandlers} />
    </QuestionnaireProvider>
  );
}
